import { AgentStep } from "@/types";
import { StepItem } from "./step-item";
import { TypingText } from "@/components/ui/styling-text";
import { useId, useState } from "react";
import { ChevronDown, ChevronUp, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

export const StepsContainer = ({
  agentSteps,
  isPlanning,
  isFinished = false,
}: {
  agentSteps?: {
    currentStep: number;
    steps: Array<AgentStep>;
  };
  isPlanning: boolean;
  isFinished?: boolean;
}) => {
  const { t } = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const contentId = useId();

  if (!isPlanning && (!agentSteps || agentSteps.steps.length === 0)) {
    return null;
  }

  const runningStep = agentSteps?.steps.find(
    (step) => step.status === "running",
  );
  const currentStepItem =
    runningStep ||
    agentSteps?.steps.find((step) => step.id === agentSteps.currentStep);

  return (
    <div
      className="flex flex-col relative transition-all duration-300 motion-reduce:transition-none rounded-xl border border-border overflow-hidden"
      style={{
        background: "var(--card-background)",
        boxShadow: "var(--message-shadow)",
      }}
    >
      {/* Header */}
      <div
        className="flex justify-between items-center px-3 sm:px-4 py-2 sm:py-3 border-b border-border gap-2"
        style={{ background: "var(--muted)" }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles size={14} className="text-primary shrink-0" />
          <span className="text-[11px] sm:text-xs font-semibold tracking-wider text-foreground uppercase truncate">
            {t("playground.actionPlan")}
          </span>
          {agentSteps && agentSteps.steps.length > 0 && (
            <span className="text-[10px] text-muted-foreground font-medium bg-background px-2 py-0.5 rounded-full shrink-0">
              {agentSteps.steps.filter((s) => s.status === "completed").length}/
              {agentSteps.steps.length}
            </span>
          )}
        </div>
        <button
          className="cursor-pointer text-[11px] sm:text-xs font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 shrink-0"
          onClick={() => setIsCollapsed(!isCollapsed)}
          type="button"
          aria-label={t(isCollapsed ? "playground.expand" : "playground.collapse")}
          aria-expanded={!isCollapsed}
          aria-controls={contentId}
        >
          {isCollapsed ? (
            <>
              <span className="hidden sm:inline">{t("playground.expand")}</span>
              <ChevronDown size={14} />
            </>
          ) : (
            <>
              <span className="hidden sm:inline">
                {t("playground.collapse")}
              </span>
              <ChevronUp size={14} />
            </>
          )}
        </button>
      </div>

      {/* Body */}
      <div className="px-3 sm:px-4 py-2 sm:py-3">
        {isCollapsed ? (
          <div>
            {isPlanning ? (
              <TypingText text={t("playground.planning")} />
            ) : !isFinished && agentSteps && currentStepItem ? (
              <StepItem
                stepNumber={currentStepItem.id}
                stepTitle={currentStepItem.title ?? ""}
                status={currentStepItem.status}
                tools={currentStepItem.tools}
              />
            ) : null}
          </div>
        ) : null}
        <div id={contentId} hidden={isCollapsed}>
          {!isCollapsed && (
            <div className="flex flex-col divide-y divide-border">
              {isPlanning && (
                <div className="py-2">
                  <TypingText text={t("playground.planning")} />
                </div>
              )}
              {agentSteps &&
                agentSteps.steps.map((step) => (
                  <div key={step.id} className="py-1 first:pt-0 last:pb-0">
                    <StepItem
                      stepNumber={step.id}
                      stepTitle={step.title ?? ""}
                      status={step.status}
                      tools={step.tools}
                    />
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
